import { PALETTES as DOCUMENTED_PALETTES } from "./designPalettes";

export type AppearanceMode = "light" | "dark" | "system";
export interface Appearance { palette: string; mode: AppearanceMode }
export const APPEARANCE_KEY = "analects-appearance-v1";
export const PALETTES = [
  { id: "ink", name: "墨黑", accent: "#ededed", background: "#000000" },
  { id: "paper", name: "紙白", accent: "#1e1e1e", background: "#ffffff" },
  ...DOCUMENTED_PALETTES,
];
type StoredAppearance = Partial<Appearance> & { background?: unknown; accent?: unknown };
function recognized(value: StoredAppearance | null): Appearance | null {
  if (PALETTES.some(p => p.id === value?.palette)) return {
    palette: value!.palette!,
    mode: ["light", "dark", "system"].includes(value?.mode ?? "") ? value!.mode! : value?.palette === "ink" ? "dark" : "light",
  };
  // Read the accepted v1 preference without rewriting it or touching legacy keys.
  const legacy = PALETTES.slice(0, 2).find(p => p.background === String(value?.background).toLowerCase() && p.accent === String(value?.accent).toLowerCase());
  return legacy ? { palette: legacy.id, mode: legacy.id === "ink" ? "dark" : "light" } : null;
}
export function sanitizeAppearance(value: StoredAppearance | null): Appearance {
  return recognized(value) ?? { palette: "ink", mode: "dark" };
}
export function loadAppearance(): Appearance {
  try {
    const saved = localStorage.getItem(APPEARANCE_KEY);
    if (saved) { const accepted = recognized(JSON.parse(saved)); if (accepted) return accepted; }
    return localStorage.getItem("lunyu-theme") === "paper" ? { palette: "paper", mode: "light" } : sanitizeAppearance(null);
  } catch { return sanitizeAppearance(null); }
}
function channels(hex: string) { return [1, 3, 5].map(at => parseInt(hex.slice(at, at + 2), 16)); }
export function mix(a: string, b: string, amount: number) {
  const ca = channels(a), cb = channels(b);
  return `#${ca.map((v, i) => Math.round(v + (cb[i] - v) * amount).toString(16).padStart(2, "0")).join("")}`;
}
function luminance(hex: string) {
  const c = channels(hex).map(v => { const s = v / 255; return s <= .04045 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4; });
  return c[0] * .2126 + c[1] * .7152 + c[2] * .0722;
}
export function contrast(a: string, b: string) { const x = luminance(a), y = luminance(b); return (Math.max(x, y) + .05) / (Math.min(x, y) + .05); }
export function readable(color: string, background: string, minimum = 4.5) {
  return fit(color, [background], minimum, contrast("#ffffff", background) > contrast("#000000", background));
}
function fit(color: string, backgrounds: string[], minimum: number, dark: boolean) {
  const target = dark ? "#ffffff" : "#000000";
  for (let step = 0; step <= 100; step++) {
    const next = mix(color, target, step / 100);
    if (backgrounds.every(background => contrast(next, background) >= minimum)) return next;
  }
  return target;
}
export function appearanceTokens(input: Appearance, systemDark = false): Record<string, string> {
  const pref = sanitizeAppearance(input);
  const dark = pref.mode === "dark" || (pref.mode === "system" && systemDark);
  const p = DOCUMENTED_PALETTES.find(p => p.id === pref.palette);
  const tint = p?.accent ?? (dark ? "#ededed" : "#1e1e1e");
  // Original light roles are snapshotted; dark roles are locally derived from
  // the same decorative accent. Every foreground is then checked on LY surfaces.
  const bg = p ? dark ? mix("#101116", tint, .12) : p.background : dark ? "#000000" : "#ffffff";
  const surface = p ? dark ? mix("#191a21", tint, .14) : p.surface : dark ? "#080808" : "#f7f7f7";
  const shade = p ? dark ? mix("#202128", tint, .16) : p.commentSurface : dark ? "#111111" : "#eeeeee";
  const soft = p ? dark ? mix("#262730", tint, .18) : mix(p.background, p.wash, .24) : dark ? "#1c1c1c" : "#e4e4e4";
  const mark = p ? dark ? mix("#2b2c34", tint, .2) : mix(p.background, p.wash, .4) : dark ? "#2d2d2d" : "#d4d4d4";
  const surfaces = [bg, surface, shade, soft, mark];
  const fg = fit(dark ? "#ededed" : p?.text ?? "#1e1e1e", surfaces, 7, dark);
  const muted = fit(dark ? "#c7c7c7" : p?.textMuted ?? "#454545", surfaces, 4.5, dark);
  const link = fit(dark ? tint : p?.link ?? "#1e1e1e", surfaces, 4.5, dark);
  return {
    "--ui-bg": bg, "--ui-fg": fg, "--ui-surface": surface, "--ui-shade": shade,
    "--ui-muted": muted, "--ui-border": mix(bg, fg, .22),
    "--ui-control": fit(mix(bg, fg, .45), surfaces, 3, dark),
    "--ui-accent": link, "--ui-accent-text": link,
    "--ui-on-accent": readable("#ffffff", link), "--ui-accent-soft": soft,
    "--ui-mark": mark, "--ui-mark-text": fg, "--ui-color-scheme": dark ? "dark" : "light",
  };
}
export function saveAppearance(value: Appearance) {
  try { localStorage.setItem(APPEARANCE_KEY, JSON.stringify(sanitizeAppearance(value))); } catch { /* Keep this session's preference. */ }
}
